import {Block, Col, Row} from 'jsxstyle'
import React from 'react'
import {ReactComponent as SightcallLogo} from '../../common/assets/sightcall-logo.svg'
import {Outlet, useNavigate} from 'react-router-dom'
import {useXpertKnowledgeContext} from './XpertKnowledgeContext'
import SearchBar from '../../components/SearchBar/SearchBar'
import {XpertKnowledgeDto, SortByEnum} from '../../services/XpertKnowledge/type'
import {XpertKnowledgeCommentContextProvider} from './XpertKnowledgeCommentContext'
import {ReactComponent as SightcallIcon} from '../../common/assets/sightcall-circle-icon.svg'
import UnpublishedTutorialsSection from './components/UnpublishedTutorialsSection'
import PublishedTutorialsSection from './components/PublishedTutorialsSection'
import {UserDto} from '../../services/User/type'
import i18n from '../../common/i18n'
import Loader from '../../components/Loader'

const XpertKnowledge: React.FC = () => {
  const {
    xpertKnowledgesPublish,
    xpertKnowledgesUnpublish,
    xpertKnowledgesBuffer,
    setSelectedXpertKnowledge,
    selectedXpertKnowledge,
    fetchXpertKnowledges,
    isLoadingUnpublish,
    activeFilters,
    setActiveFilters,
  } = useXpertKnowledgeContext()

  const navigate = useNavigate()

  const handleSearch = (query?: string) => {
    const newFilters = {
      ...activeFilters,
      title: query,
    }

    setActiveFilters(newFilters)

    fetchXpertKnowledges(
      newFilters.userId,
      newFilters.equipmentType,
      newFilters.startTimestamp,
      newFilters.endTimestamp,
      newFilters.sortBy,
      undefined,
      query
    )
  }

  const handleRowClick = (xpertKnowledge: XpertKnowledgeDto) => {
    setSelectedXpertKnowledge(xpertKnowledge)
    navigate(`${xpertKnowledge.id}/tutorial`)
  }

  const handleFieldChange = (
    selectedCreator?: UserDto,
    selectedEquipmentType?: string,
    startTimestamp?: string,
    endTimestamp?: string,
    sortBy?: SortByEnum
  ) => {
    const newFilters = {...activeFilters}

    if (selectedCreator !== undefined) {
      if (selectedCreator && selectedCreator.fullName !== 'All') {
        newFilters.userId = selectedCreator.id
      } else {
        newFilters.userId = undefined
      }
    }

    if (selectedEquipmentType !== undefined) {
      if (selectedEquipmentType && selectedEquipmentType !== 'All') {
        newFilters.equipmentType = selectedEquipmentType.replace(/'/g, '`')
      } else {
        newFilters.equipmentType = undefined
      }
    }

    if (startTimestamp !== undefined) {
      newFilters.startTimestamp = startTimestamp
    }
    if (endTimestamp !== undefined) {
      newFilters.endTimestamp = endTimestamp
    }

    if (sortBy !== undefined) {
      newFilters.sortBy = sortBy
    }

    setActiveFilters(newFilters)
    fetchXpertKnowledges(
      newFilters.userId,
      newFilters.equipmentType,
      newFilters.startTimestamp,
      newFilters.endTimestamp,
      newFilters.sortBy,
      undefined,
      newFilters.title
    )
  }

  const handleDateRangeReset = () => {
    const newFilters = {
      ...activeFilters,
      startTimestamp: undefined,
      endTimestamp: undefined,
    }
    setActiveFilters(newFilters)
    fetchXpertKnowledges(
      newFilters.userId,
      newFilters.equipmentType,
      newFilters.startTimestamp,
      newFilters.endTimestamp,
      newFilters.sortBy,
      undefined,
      newFilters.title
    )
  }

  const handleResetFilters = () => {
    const resetFilters = {
      userId: undefined,
      equipmentType: undefined,
      startTimestamp: undefined,
      endTimestamp: undefined,
      sortBy: SortByEnum.DEFAULT,
      title: undefined,
    }
    setActiveFilters(resetFilters)
    fetchXpertKnowledges()
  }

  return (
    <Col padding="20px 94px" gap="20px" boxSizing="border-box" width="100%">
      <Col gap="20px" width="100%">
        <Row gap="10px" alignItems="baseline">
          <SightcallLogo width={36} height={16} />
          <Block fontSize="24px" fontWeight={500} lineHeight="64px">
            {i18n.t('xk.list.title')}
          </Block>
        </Row>
        <Row justifyContent="space-between" alignItems="center" gap="20px">
          <SearchBar placeholder="Search" onSearch={handleSearch} initialValue={activeFilters.title} />
        </Row>

        <Col height="calc(100vh - 260px)" gap="20px" overflowY="auto">
          {isLoadingUnpublish ? (
            <Col height="calc(100vh - 290px)" width="100%" justifyContent="center" alignItems="center">
              <Loader size={75} />
            </Col>
          ) : (
            <>
              {(xpertKnowledgesBuffer?.length || 0) + (xpertKnowledgesPublish?.length || 0) + (xpertKnowledgesUnpublish?.length || 0) > 0 ? (
                <>
                  {xpertKnowledgesUnpublish.length > 0 && (
                    <UnpublishedTutorialsSection
                      tutorials={xpertKnowledgesUnpublish.concat(xpertKnowledgesBuffer)}
                      selectedId={selectedXpertKnowledge?.id}
                      onTutorialClick={handleRowClick}
                    />
                  )}
                  <PublishedTutorialsSection
                    tutorials={xpertKnowledgesPublish}
                    selectedId={selectedXpertKnowledge?.id}
                    onTutorialClick={handleRowClick}
                    onCreatorChange={(value) => handleFieldChange(value, undefined, undefined, undefined, undefined)}
                    onEquipmentTypeChange={(value) => handleFieldChange(undefined, value, undefined, undefined, undefined)}
                    onSortChange={(value) => handleFieldChange(undefined, undefined, undefined, undefined, value)}
                    onDateRangeChange={(start, end) => handleFieldChange(undefined, undefined, start, end, undefined)}
                    onDateRangeReset={handleDateRangeReset}
                    currentCreatorId={activeFilters.userId}
                    currentEquipmentType={activeFilters.equipmentType}
                    currentSortBy={activeFilters.sortBy}
                    currentStartDate={activeFilters.startTimestamp}
                    currentEndDate={activeFilters.endTimestamp}
                  />
                </>
              ) : (
                <Col justifyContent="center" alignItems="center" gap="20px" height="100%">
                  <SightcallIcon width={175} height={175} />
                  <Block fontSize="34px" fontWeight={500}>
                    {i18n.t('xk.list.noData')}
                  </Block>
                </Col>
              )}
            </>
          )}
        </Col>
      </Col>

      {selectedXpertKnowledge && (
        <XpertKnowledgeCommentContextProvider xpertKnowledgeId={selectedXpertKnowledge.id}>
          <Outlet />
        </XpertKnowledgeCommentContextProvider>
      )}
    </Col>
  )
}

export default XpertKnowledge
